// ./src/components/generic/AuditInfo/AuditInfo.tsx

// ============================================
// AuditInfo — Generic audit trail component
// ============================================
// Supports records with any combination of:
//   user, userCreated, userUpdated, userRemoved, userApproved, userRejected
// ============================================

import React from "react";
import { Group, GroupProps, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { AppIcon } from "@/components/generic/AppIcon";
import { User } from "@/utils/api/UserService";
import { buildFullName } from "@/utils/nameHelpers";

export type AuditableRecord = {
  user?: User | null;
  userCreated?: User | null;
  userUpdated?: User | null;
  userRemoved?: User | null;
  userApproved?: User | null;
  userRejected?: User | null;
};

interface AuditEntry {
  user: User;
  iconName: string;
  labelKey: string;
  labelDefault: string;
}

interface AuditInfoProps extends GroupProps {
  record: AuditableRecord;
}

const AuditInfoComponent: React.FC<AuditInfoProps> = ({ record, ...groupProps }) => {
  const { t } = useTranslation(["common"]);

  const creatorUser = record.userCreated ?? record.user ?? null;
  const updaterUser = record.userUpdated ?? null;
  const removerUser = record.userRemoved ?? null;
  const approverUser = record.userApproved ?? null;
  const rejecterUser = record.userRejected ?? null;

  const showUpdater =
    updaterUser != null &&
    (creatorUser == null || updaterUser.id !== creatorUser.id);

  const entries: AuditEntry[] = [];

  if (creatorUser) {
    entries.push({
      user: creatorUser,
      iconName: "UserPlus",
      labelKey: "common:labels.createdBy",
      labelDefault: "Created by {{name}}",
    });
  }

  if (showUpdater && updaterUser) {
    entries.push({
      user: updaterUser,
      iconName: "UserEdit",
      labelKey: "common:labels.updatedBy",
      labelDefault: "Updated by {{name}}",
    });
  }

  if (removerUser) {
    entries.push({
      user: removerUser,
      iconName: "UserMinus",
      labelKey: "common:labels.removedBy",
      labelDefault: "Removed by {{name}}",
    });
  }

  if (approverUser) {
    entries.push({
      user: approverUser,
      iconName: "UserCheck",
      labelKey: "common:labels.approvedBy",
      labelDefault: "Approved by {{name}}",
    });
  }

  if (rejecterUser) {
    entries.push({
      user: rejecterUser,
      iconName: "UserX",
      labelKey: "common:labels.rejectedBy",
      labelDefault: "Rejected by {{name}}",
    });
  }

  if (entries.length === 0) return null;

  return (
    <Group gap="md" wrap="wrap" {...groupProps}>
      {entries.map(({ user, iconName, labelKey, labelDefault }) => (
        <Group key={labelKey} gap={6} align="center">
          <AppIcon iconName={iconName} size={18} color="var(--mantine-color-gray-8)" />
          <Text size="md" c="dimmed">
            {t(labelKey, { name: buildFullName(user), defaultValue: labelDefault })}
          </Text>
        </Group>
      ))}
    </Group>
  );
};

export const AuditInfo = React.memo(AuditInfoComponent);